import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateOrReplaceAllMembershipQuestionsOptions,
  CreateOrReplaceAllMembershipQuestionsResponse,
  ListAnswersOptions,
  ListAnswersResponse,
  ListMembershipQuestionsResponse,
  createOrReplaceAllMembershipQuestions as universalCreateOrReplaceAllMembershipQuestions,
  listAnswers as universalListAnswers,
  listMembershipQuestions as universalListMembershipQuestions,
} from './social-groups-v2-membership-question-membership-questions.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/groups' };

export function listMembershipQuestions(
  httpClient: HttpClient
): ListMembershipQuestionsSignature {
  return (groupId: string) =>
    universalListMembershipQuestions(
      groupId,
      // @ts-ignore
      { httpClient }
    );
}

interface ListMembershipQuestionsSignature {
  /**
   * Retrieves the membership questions for a group.
   *
   * >**Note:**
   * >This endpoint requires [visitor or member authentication](https://dev.wix.com/docs/rest/articles/getting-started/access-types-and-permissions).
   * @param - Group ID.
   */
  (groupId: string): Promise<
    NonNullablePaths<
      ListMembershipQuestionsResponse,
      `questions` | `questions.${number}.required` | `questions.${number}.text`,
      4
    >
  >;
}

export function createOrReplaceAllMembershipQuestions(
  httpClient: HttpClient
): CreateOrReplaceAllMembershipQuestionsSignature {
  return (
    groupId: string,
    options?: CreateOrReplaceAllMembershipQuestionsOptions
  ) =>
    universalCreateOrReplaceAllMembershipQuestions(
      groupId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateOrReplaceAllMembershipQuestionsSignature {
  /**
   * Creates membership questions if none have been set up. Otherwise, replaces all existing questions.
   *
   * Providing an empty array means that members won't have to answer any question when joining the group.
   * Only admins can create or replace membership questions.
   *
   * >**Note:**
   * >This endpoint requires [visitor or member authentication](https://dev.wix.com/docs/rest/articles/getting-started/access-types-and-permissions).
   * @param - Group ID.
   */
  (
    groupId: string,
    options?: CreateOrReplaceAllMembershipQuestionsOptions
  ): Promise<
    NonNullablePaths<
      CreateOrReplaceAllMembershipQuestionsResponse,
      `questions` | `questions.${number}.required` | `questions.${number}.text`,
      4
    >
  >;
}

export function listAnswers(httpClient: HttpClient): ListAnswersSignature {
  return (groupId: string, options?: ListAnswersOptions) =>
    universalListAnswers(
      groupId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListAnswersSignature {
  /**
   * Retrieves the answers to the membership questions, given the provided filters.
   *
   * >**Note:**
   * >This endpoint requires [visitor or member authentication](https://dev.wix.com/docs/rest/articles/getting-started/access-types-and-permissions).
   * @param - Group ID.
   */
  (groupId: string, options?: ListAnswersOptions): Promise<
    NonNullablePaths<
      ListAnswersResponse,
      `memberAnswers` | `memberAnswers.${number}.memberId`,
      4
    >
  >;
}

export {
  CreateOrReplaceAllMembershipQuestionsOptions,
  CreateOrReplaceAllMembershipQuestionsRequest,
  CreateOrReplaceAllMembershipQuestionsResponse,
  ListAnswerCountsRequest,
  ListAnswerCountsResponse,
  ListAnswersOptions,
  ListAnswersRequest,
  ListAnswersResponse,
  ListMembershipQuestionsRequest,
  ListMembershipQuestionsResponse,
  MemberAnswerCount,
  MemberMembershipQuestionAnswers,
  MembershipQuestion,
  MembershipQuestionAnswer,
  Paging,
  PagingMetadata,
} from './social-groups-v2-membership-question-membership-questions.universal.js';
